
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EquipamentosSelector } from "@/components/orcamentos/EquipamentosSelector";
import { EditarOrcamentoDialog } from "@/components/EditarOrcamentoDialog";
import { useQueryClient } from "@tanstack/react-query";
import { Search, Eye, Download, Check, Edit, Trash2, Clock, Plus, XCircle, ExternalLink, Paperclip, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Orcamento, type OrcamentosSort } from "@/hooks/useOrcamentos";
import { PlanoSeguro } from "@/hooks/usePlanos";
import { toast } from "sonner";
import { useUserNamesByIds } from "@/hooks/useUserNamesByIds";
import { openStorageUrl } from "@/lib/storageUrl";
import { useCidadesPorEstado } from "@/hooks/useClientesEstadosCidades";
import { BRASIL_UFS } from "@/lib/estados";
import { ListagemAcoes, type ListagemColumn } from "@/components/listagem/ListagemAcoes";
import { listagemThLeft, listagemThCenter, listagemTdLeft, listagemTdCenter } from "@/components/listagem/listagemTableClasses";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface OrcamentosListProps {
  orcamentosFiltrados: Orcamento[];
  loadingOrcamentos: boolean;
  buscaOrcamento: string;
  setBuscaOrcamento: (value: string) => void;
  buscaOrcamentoQuery: string;
  filtrosDraft: {
    dataInicio: string;
    dataFim: string;
    planoId: string;
    status: string;
    estado: string;
    cidade: string;
    valorProjetoMin: string;
    valorProjetoMax: string;
    valorSeguroMin: string;
    valorSeguroMax: string;
    paineisMin: string;
    paineisMax: string;
  };
  setFiltrosDraft: (next: {
    dataInicio: string;
    dataFim: string;
    planoId: string;
    status: string;
    estado: string;
    cidade: string;
    valorProjetoMin: string;
    valorProjetoMax: string;
    valorSeguroMin: string;
    valorSeguroMax: string;
    paineisMin: string;
    paineisMax: string;
  }) => void;
  filtrosAplicados: {
    dataInicio: string;
    dataFim: string;
    planoId: string;
    status: string;
    estado: string;
    cidade: string;
    valorProjetoMin: string;
    valorProjetoMax: string;
    valorSeguroMin: string;
    valorSeguroMax: string;
    paineisMin: string;
    paineisMax: string;
  };
  onApplyFilters: () => void;
  onClearFilters: () => void;
  currentUserId: string | null;
  sort: OrcamentosSort;
  onToggleSort: (field: OrcamentosSort["field"]) => void;
  handleGerarPdf: (orcamento: Orcamento) => void;
  handleAprovarOrcamento: (orcamentoId: number) => void;
  handleExcluirOrcamento: (orcamentoId: number) => void;
  handleRejeitarOrcamentoVistoria?: (orcamentoId: number, motivo?: string) => void;
  isPendingAprovacao: boolean;
  isPendingRejeicao?: boolean;
  isVendedor?: boolean;
  orcamentos?: Orcamento[];
  planos?: PlanoSeguro[];
  onCreateOrcamento?: () => void;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

export function OrcamentosList({
  orcamentosFiltrados,
  loadingOrcamentos,
  buscaOrcamento,
  setBuscaOrcamento,
  buscaOrcamentoQuery,
  filtrosDraft,
  setFiltrosDraft,
  filtrosAplicados,
  onApplyFilters,
  onClearFilters,
  currentUserId,
  sort,
  onToggleSort,
  handleGerarPdf,
  handleAprovarOrcamento,
  handleExcluirOrcamento,
  handleRejeitarOrcamentoVistoria,
  isPendingAprovacao,
  isPendingRejeicao = false,
  isVendedor = false,
  orcamentos,
  planos,
  onCreateOrcamento,
  currentPage: currentPageProp = 1,
  totalPages: totalPagesProp = 1,
  totalItems: totalItemsProp = 0,
  onPageChange
}: OrcamentosListProps) {
  const queryClient = useQueryClient();
  const { data: cidadesDoEstado = [] } = useCidadesPorEstado(filtrosDraft.estado);
  const [orcamentoExpandido, setOrcamentoExpandido] = useState<number | null>(null);
  const [showEditarOrcamento, setShowEditarOrcamento] = useState(false);
  const [orcamentoParaEditar, setOrcamentoParaEditar] = useState<Orcamento | null>(null);
  const [rejeitarOrcamentoId, setRejeitarOrcamentoId] = useState<number | null>(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState("");
  const [orcamentoAnexosVistoria, setOrcamentoAnexosVistoria] = useState<Orcamento | null>(null);
  
  // Usar props de paginação se fornecidas, senão usar estado local (compatibilidade)
  const currentPage = onPageChange ? currentPageProp : 1;
  const totalPages = totalPagesProp || 1;
  const currentItems = orcamentosFiltrados; // Já vem paginado do backend

  const { data: userNamesMap } = useUserNamesByIds(
    currentItems.flatMap((o) => [o.vendedor_id, o.desconto_concedido_por])
  );
  const getClienteLabel = (o: Orcamento) =>
    o.cliente?.nome || (o.cliente_id ? `Cliente ID: ${o.cliente_id}` : "Cliente não encontrado");
  type OrcamentoRow = Orcamento & {
    criado_por: string;
    plano_label: string;
    vigencia_label: string;
    data_label: string;
    status_label: string;
    valor_projeto_label: string;
    valor_seguro_label: string;
    desconto_label: string;
    paineis_label: string;
  };
  const orcColumns: ListagemColumn<OrcamentoRow>[] = [
    { key: "id", label: "ID", value: (o) => o.id },
    { key: "cliente", label: "Cliente", value: (o) => getClienteLabel(o) },
    { key: "localizacao", label: "Estado/Cidade", value: (o) => `${o.cliente?.estado || "—"} / ${o.cliente?.cidade || "—"}` },
    { key: "criado_por", label: "Criado por", value: (o) => o.criado_por },
    { key: "valor_projeto", label: "Valor do Projeto", value: (o) => o.valor_projeto_label },
    { key: "valor_seguro", label: "Valor do Seguro", value: (o) => o.valor_seguro_label },
    { key: "desconto", label: "Desconto", value: (o) => o.desconto_label },
    { key: "paineis", label: "Painéis", value: (o) => o.paineis_label },
    { key: "plano", label: "Plano", value: (o) => o.plano_label },
    { key: "vigencia", label: "Vigência", value: (o) => o.vigencia_label },
    { key: "data", label: "Data", value: (o) => o.data_label },
    { key: "status", label: "Status", value: (o) => o.status_label },
  ];
  const [visibleOrcCols, setVisibleOrcCols] = useState<string[]>(orcColumns.map((c) => c.key));
  const toggleOrcCol = (key: string, nextVisible: boolean) => {
    setVisibleOrcCols((prev) => (nextVisible ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));
  };

  // Ordenação vem do backend (mesma lógica de Clientes)
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aprovado': return 'bg-green-100 text-green-800';
      case 'pendente': return 'bg-yellow-100 text-yellow-800';
      case 'rejeitado': return 'bg-red-100 text-red-800';
      case 'aguardando_aprovacao_vistoria': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    if (status === 'aguardando_aprovacao_vistoria') return 'Aguard. aprovação vistoria';
    return status === 'aprovado' ? 'Aprovado' : status === 'pendente' ? 'Pendente' : status === 'rejeitado' ? 'Rejeitado' : status;
  }

  const getPlanoNome = (planoId: string) => {
    if (!planoId || !planos) return '-';
    
    const plano = planos.find(p => p.id === planoId);
    if (!plano) {
      // Fallback para nomes antigos
      switch (planoId) {
        case 'basico': return 'Básico';
        case 'elevo': return 'Elevo';
        case 'renovacao-elevo': return 'Renovação Elevo';
        case 'personalizado': return 'Personalizado';
        default: return planoId;
      }
    }
    return plano.nome;
  };

  const getPlanoColor = (planoId: string) => {
    if (!planoId || !planos) return 'bg-gray-100 text-gray-800';
    
    const plano = planos.find(p => p.id === planoId);
    if (!plano) {
      // Fallback para cores antigas
      switch (planoId) {
        case 'elevo': return 'bg-blue-100 text-blue-800';
        case 'renovacao-elevo': return 'bg-purple-100 text-purple-800';
        case 'personalizado': return 'bg-orange-100 text-orange-800';
        case 'basico': return 'bg-green-100 text-green-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
    
    // Cores baseadas no nome do plano
    const nomePlano = plano.nome.toLowerCase();
    if (nomePlano.includes('elevo')) return 'bg-blue-100 text-blue-800';
    if (nomePlano.includes('renovação') || nomePlano.includes('renovacao')) return 'bg-purple-100 text-purple-800';
    if (nomePlano.includes('personalizado')) return 'bg-orange-100 text-orange-800';
    if (nomePlano.includes('básico') || nomePlano.includes('basico')) return 'bg-green-100 text-green-800';
    
    return 'bg-gray-100 text-gray-800';
  };

  // Função para calcular tempo restante até o prazo de aprovação
  const getTempoRestante = (orcamento: Orcamento) => {
    if (!orcamento.prazo_aprovacao || orcamento.status !== 'pendente') return null;
    
    const prazo = new Date(orcamento.prazo_aprovacao);
    const agora = new Date();
    const diffMs = prazo.getTime() - agora.getTime();
    
    if (diffMs <= 0) return 'VENCIDO';
    
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    
    if (diffDays > 1) return `${diffDays} dias`;
    if (diffDays === 1) return '1 dia';
    if (diffHours > 1) return `${diffHours} horas`;
    return 'Menos de 1 hora';
  };

  // Função para obter cor do tempo restante
  const getTempoRestanteColor = (orcamento: Orcamento) => {
    if (!orcamento.prazo_aprovacao || orcamento.status !== 'pendente') return 'text-gray-500';
    
    const prazo = new Date(orcamento.prazo_aprovacao);
    const agora = new Date();
    const diffMs = prazo.getTime() - agora.getTime();
    
    if (diffMs <= 0) return 'text-red-600 font-bold';
    if (diffMs <= 24 * 60 * 60 * 1000) return 'text-orange-600 font-semibold'; // 1 dia ou menos
    if (diffMs <= 3 * 24 * 60 * 60 * 1000) return 'text-yellow-600 font-semibold'; // 3 dias ou menos
    return 'text-green-600';
  };

  const extrairDadosDoPlano = (observacoes: string) => {
    if (!observacoes) return { quantidade_placas: 0, tipo_plano: '', vigencia_anos: 0 };
    
    const quantidadeMatch = observacoes.match(/Quantidade de painéis: (\d+)/);
    const planoMatch = observacoes.match(/Plano: ([^,]+)/);
    const vigenciaMatch = observacoes.match(/Vigência: (\d+) anos?/);
    
    return {
      quantidade_placas: quantidadeMatch ? parseInt(quantidadeMatch[1]) : 0,
      tipo_plano: planoMatch ? planoMatch[1].trim() : '',
      vigencia_anos: vigenciaMatch ? parseInt(vigenciaMatch[1]) : 0
    };
  };

  const mapOrcamentoRow = (o: Orcamento, namesMap?: Record<string, string>): OrcamentoRow => {
    const valor_projeto_label = `R$ ${Number(o.valor_total || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const valor_seguro_label =
      o.valor_seguro && o.valor_seguro > 0
        ? `R$ ${Number(o.valor_seguro).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : "-";
    const qtd = Number(o.quantidade_placas) || 0;
    const valorPainel = Number(o.plano?.valor_por_painel) || 0;
    const vigencia = Number(o.vigencia_anos) || 1;
    const multVigencia =
      vigencia === 2 ? 1.8 : vigencia === 3 ? 2.5 : vigencia === 5 ? 3.8 : vigencia === 10 ? 6.5 : 1;
    const valorTabelaInferido =
      o.valor_tabela != null && Number(o.valor_tabela) > 0
        ? Number(o.valor_tabela)
        : qtd > 0 && valorPainel > 0
          ? qtd * valorPainel * multVigencia
          : 0;
    const valorSeguroNum = Number(o.valor_seguro) || 0;
    let descontoNum = Number(o.desconto_valor) || 0;
    if (descontoNum <= 0 && valorTabelaInferido > 0 && valorSeguroNum > 0 && valorTabelaInferido > valorSeguroNum + 0.009) {
      descontoNum = Math.round((valorTabelaInferido - valorSeguroNum) * 100) / 100;
    }
    const quemDesconto = o.desconto_concedido_por
      ? namesMap?.[o.desconto_concedido_por] || "—"
      : "";
    const desconto_label =
      descontoNum > 0
        ? `R$ ${descontoNum.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${quemDesconto ? ` (${quemDesconto})` : ""}`
        : "-";
    const paineis_label = o.quantidade_placas && o.quantidade_placas > 0 ? `${o.quantidade_placas}` : "-";
    const plano_label = o.plano_id ? getPlanoNome(o.plano_id) : "-";
    const vigencia_label = o.vigencia_anos && o.vigencia_anos > 0 ? `${o.vigencia_anos}` : "-";
    const data_label = o.created_at ? new Date(o.created_at).toLocaleDateString("pt-BR") : "-";
    const status_label = getStatusLabel(o.status);
    const criado_por = o.vendedor_id ? (namesMap?.[o.vendedor_id] || "—") : "—";
    return {
      ...o,
      valor_projeto_label,
      valor_seguro_label,
      desconto_label,
      paineis_label,
      plano_label,
      vigencia_label,
      data_label,
      status_label,
      criado_por,
    } as OrcamentoRow;
  };

  const fetchAllForActions = async (): Promise<OrcamentoRow[]> => {
    const batch = 1000;
    let from = 0;
    const all: Orcamento[] = [];

    // Base query — inner no cliente quando há filtro estado/cidade
    const clienteFilterPresentExp = !!(filtrosAplicados.estado || filtrosAplicados.cidade);
    const clienteEmbedExp = `cliente:clientes${clienteFilterPresentExp ? "!inner" : ""}(id, nome, email, telefone, cpf_cnpj, endereco, numero, bairro, cidade, estado, cep)`;

    let base = supabase
      .from("orcamentos")
      .select(
        `
          *,
          ${clienteEmbedExp},
          plano:planos_seguro(*),
          equipamentos:orcamento_equipamentos(
            *,
            equipamento:equipamentos(*)
          )
        `
      )
      .eq("ativo", true);

    if (isVendedor && currentUserId) {
      base = base.eq("vendedor_id", currentUserId);
    }

    // Applied filters
    if (filtrosAplicados.dataInicio) {
      base = base.gte("created_at", `${filtrosAplicados.dataInicio}T00:00:00`);
    }
    if (filtrosAplicados.dataFim) {
      base = base.lte("created_at", `${filtrosAplicados.dataFim}T23:59:59`);
    }
    if (filtrosAplicados.planoId) {
      base = base.eq("plano_id", filtrosAplicados.planoId);
    }
    if (filtrosAplicados.status) {
      base = base.eq("status", filtrosAplicados.status);
    }
    if (filtrosAplicados.estado) {
      base = base.eq("cliente.estado", filtrosAplicados.estado.toUpperCase());
    }
    if (filtrosAplicados.cidade) {
      base = base.eq("cliente.cidade", filtrosAplicados.cidade);
    }
    if (filtrosAplicados.valorProjetoMin) {
      base = base.gte("valor_total", Number(filtrosAplicados.valorProjetoMin));
    }
    if (filtrosAplicados.valorProjetoMax) {
      base = base.lte("valor_total", Number(filtrosAplicados.valorProjetoMax));
    }
    if (filtrosAplicados.valorSeguroMin) {
      base = base.gte("valor_seguro", Number(filtrosAplicados.valorSeguroMin));
    }
    if (filtrosAplicados.valorSeguroMax) {
      base = base.lte("valor_seguro", Number(filtrosAplicados.valorSeguroMax));
    }
    if (filtrosAplicados.paineisMin) {
      base = base.gte("quantidade_placas", Number(filtrosAplicados.paineisMin));
    }
    if (filtrosAplicados.paineisMax) {
      base = base.lte("quantidade_placas", Number(filtrosAplicados.paineisMax));
    }

    // Search (same logic as hook)
    const searchTerm = (buscaOrcamentoQuery || "").trim();
    if (searchTerm) {
      if (!isNaN(Number(searchTerm))) {
        base = base.eq("id", parseInt(searchTerm));
      } else {
        let clientesQuery = supabase.from("clientes").select("id").ilike("nome", `%${searchTerm}%`);
        if (isVendedor && currentUserId) clientesQuery = clientesQuery.eq("vendedor_id", currentUserId);
        const { data: clientesEncontrados } = await clientesQuery;
        if (clientesEncontrados && clientesEncontrados.length > 0) {
          const clienteIds = clientesEncontrados.map((c: any) => c.id);
          base = base.in("cliente_id", clienteIds);
        } else {
          base = base.eq("id", -1);
        }
      }
    }

    // Fetch in batches
    while (true) {
      const to = from + batch - 1;
      const { data, error } = await base.order("created_at", { ascending: false }).range(from, to);
      if (error) throw error;
      const page = ((data || []) as unknown) as Orcamento[];
      all.push(...page);
      if (page.length < batch) break;
      from += batch;
    }

    // Map vendor / discount author ids -> names for all rows
    const ids = Array.from(
      new Set(
        all
          .flatMap((o) => [o.vendedor_id, o.desconto_concedido_por])
          .filter((id): id is string => typeof id === "string" && Boolean(id.trim()))
      )
    );
    const namesMap: Record<string, string> = {};
    const chunkSize = 1000;
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const { data: profs, error: profErr } = await supabase.from("profiles").select("id, nome").in("id", chunk);
      if (profErr) throw profErr;
      for (const row of (profs || []) as Array<{ id: string; nome: string | null }>) {
        if (row?.id) namesMap[row.id] = row.nome || "Usuário";
      }
    }

    return all.map((o) => mapOrcamentoRow(o, namesMap));
  };

  const handleEditarOrcamento = (orcamento: Orcamento) => {
    setOrcamentoParaEditar(orcamento);
    setShowEditarOrcamento(true);
  };

  return (
    <>
      <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <Search className="h-5 w-5 text-green-600" />
            Orçamentos Gerados
          </CardTitle>
          <div className="flex gap-2">
            <ListagemAcoes
              title="Orçamentos"
              subtitle={`Página ${currentPage} de ${totalPages}`}
              rows={currentItems.map((o) => mapOrcamentoRow(o, userNamesMap))}
              fetchAllRows={fetchAllForActions}
              columns={orcColumns}
              visibleKeys={visibleOrcCols}
              onToggleColumn={toggleOrcCol}
              filenameBase={`orcamentos_p${currentPage}`}
            />
            <Button 
              onClick={onCreateOrcamento}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Plus className="h-4 w-4 mr-2" />
              Criar Orçamento
            </Button>
          </div>
        </div>

        <div className="mt-4 bg-[#C9C9C9] rounded-lg p-4 text-xs">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label>Buscar</Label>
              <Input
                placeholder="Buscar por nome ou ID..."
                value={buscaOrcamento}
                onChange={(e) => setBuscaOrcamento(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
                className="h-8 text-xs"
              />
            </div>

            <div className="md:col-span-2">
              <Label>Período - início</Label>
              <Input
                type="date"
                value={filtrosDraft.dataInicio}
                onChange={(e) => setFiltrosDraft({ ...filtrosDraft, dataInicio: e.target.value })}
                className="h-8 text-xs"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Período - fim</Label>
              <Input
                type="date"
                value={filtrosDraft.dataFim}
                onChange={(e) => setFiltrosDraft({ ...filtrosDraft, dataFim: e.target.value })}
                className="h-8 text-xs"
              />
            </div>

            <div className="md:col-span-2">
              <Label>Plano</Label>
              <select
                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                value={filtrosDraft.planoId}
                onChange={(e) => setFiltrosDraft({ ...filtrosDraft, planoId: e.target.value })}
              >
                <option value="">Todos</option>
                {(planos || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Status</Label>
              <select
                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                value={filtrosDraft.status}
                onChange={(e) => setFiltrosDraft({ ...filtrosDraft, status: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="pendente">Pendente</option>
                <option value="aprovado">Aprovado</option>
                <option value="rejeitado">Rejeitado</option>
                <option value="aguardando_aprovacao_vistoria">Aguard. aprovação vistoria</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <Label>Estado</Label>
              <select
                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                value={filtrosDraft.estado}
                onChange={(e) => setFiltrosDraft({ ...filtrosDraft, estado: e.target.value, cidade: "" })}
              >
                <option value="">Todos</option>
                {BRASIL_UFS.map((uf) => (
                  <option key={uf.sigla} value={uf.sigla}>
                    {uf.sigla} - {uf.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <Label>Cidade</Label>
              <select
                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs disabled:opacity-60"
                value={filtrosDraft.cidade}
                onChange={(e) => setFiltrosDraft({ ...filtrosDraft, cidade: e.target.value })}
                disabled={!filtrosDraft.estado}
              >
                <option value="">{filtrosDraft.estado ? "Todas" : "Selecione um estado"}</option>
                {(cidadesDoEstado || []).map((c: string) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-4">
              <Label>Painéis (mín - máx)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  step="1"
                  placeholder="Min"
                  value={filtrosDraft.paineisMin}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, paineisMin: e.target.value })}
                  className="h-8 text-xs"
                />
                <Input
                  type="number"
                  inputMode="numeric"
                  step="1"
                  placeholder="Máx"
                  value={filtrosDraft.paineisMax}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, paineisMax: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
            </div>

            <div className="md:col-span-4">
              <Label>Valor do Projeto (mín - máx)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="Min"
                  value={filtrosDraft.valorProjetoMin}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorProjetoMin: e.target.value })}
                  className="h-8 text-xs"
                />
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="Máx"
                  value={filtrosDraft.valorProjetoMax}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorProjetoMax: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
            </div>

            <div className="md:col-span-4">
              <Label>Valor do Seguro (mín - máx)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="Min"
                  value={filtrosDraft.valorSeguroMin}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorSeguroMin: e.target.value })}
                  className="h-8 text-xs"
                />
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="Máx"
                  value={filtrosDraft.valorSeguroMax}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorSeguroMax: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
            </div>

            <div className="md:col-span-12 flex items-end justify-end gap-2">
              <Button variant="outline" type="button" onClick={onClearFilters} className="h-8 px-3 text-xs">
                Limpar
              </Button>
              <Button
                type="button"
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 px-3 text-xs"
                onClick={onApplyFilters}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loadingOrcamentos ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p>Carregando orçamentos...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-[11px] leading-tight">
              <thead>
                <tr className="border-b">
                  {visibleOrcCols.includes("id") && (
                    <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap")} onClick={() => onToggleSort("id")}>
                      ID {sort.field === "id" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleOrcCols.includes("cliente") && (
                    <th className={cn(listagemThLeft, "cursor-pointer select-none")} onClick={() => onToggleSort("cliente_nome")}>
                      Cliente {sort.field === "cliente_nome" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleOrcCols.includes("localizacao") && (
                    <th className={cn(listagemThLeft, "whitespace-nowrap hidden md:table-cell")}>
                      Estado/Cidade
                    </th>
                  )}
                  {visibleOrcCols.includes("criado_por") && (
                    <th className={cn(listagemThLeft, "hidden lg:table-cell")}>Criado por</th>
                  )}
                  {visibleOrcCols.includes("valor_projeto") && (
                    <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden lg:table-cell")} onClick={() => onToggleSort("valor_total")}>
                      Valor do Projeto {sort.field === "valor_total" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleOrcCols.includes("valor_seguro") && (
                    <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden lg:table-cell")} onClick={() => onToggleSort("valor_seguro")}>
                      Valor do Seguro {sort.field === "valor_seguro" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleOrcCols.includes("desconto") && (
                    <th className={cn(listagemThCenter, "whitespace-nowrap hidden lg:table-cell")}>
                      Desconto
                    </th>
                  )}
                  {visibleOrcCols.includes("paineis") && (
                    <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden md:table-cell")} onClick={() => onToggleSort("quantidade_placas")}>
                      Painéis {sort.field === "quantidade_placas" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleOrcCols.includes("plano") && (
                    <th className={cn(listagemThLeft, "cursor-pointer select-none whitespace-nowrap hidden md:table-cell")} onClick={() => onToggleSort("plano_id")}>
                      Plano {sort.field === "plano_id" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleOrcCols.includes("vigencia") && (
                    <th className={cn(listagemThCenter, "hidden lg:table-cell whitespace-nowrap")}>Vigência</th>
                  )}
                  {visibleOrcCols.includes("data") && (
                    <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden md:table-cell")} onClick={() => onToggleSort("created_at")}>
                      Data {sort.field === "created_at" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleOrcCols.includes("status") && (
                    <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap")} onClick={() => onToggleSort("status")}>
                      Status {sort.field === "status" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  <th className={cn(listagemThCenter, "hidden lg:table-cell whitespace-nowrap")}>Prazo Aprovação</th>
                  <th className={cn(listagemThCenter, "whitespace-nowrap")}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((orcamento) => {
                  return (
                    <>
                      <tr key={orcamento.id} className="border-b odd:bg-gray-50/60 hover:bg-gray-50">
                      {visibleOrcCols.includes("id") && <td className={cn(listagemTdCenter, "text-gray-900 font-semibold whitespace-nowrap")}>{orcamento.id}</td>}
                      {visibleOrcCols.includes("cliente") && (
                        <td className={cn(listagemTdLeft, "text-gray-900")}>
                          <div className="max-w-[260px] truncate font-medium">{getClienteLabel(orcamento)}</div>
                          <div className="text-[10px] text-gray-500 md:hidden">
                            {(orcamento.cliente?.estado || "—").trim()} / {(orcamento.cliente?.cidade || "—").trim()}
                          </div>
                        </td>
                      )}
                      {visibleOrcCols.includes("localizacao") && (
                        <td className={cn(listagemTdLeft, "text-gray-600 whitespace-nowrap hidden md:table-cell")}>
                          {orcamento.cliente?.estado || "—"} / {orcamento.cliente?.cidade || "—"}
                        </td>
                      )}
                      {visibleOrcCols.includes("criado_por") && (
                        <td className={cn(listagemTdCenter, "text-gray-600 hidden lg:table-cell")}>
                          {orcamento.vendedor_id ? (userNamesMap?.[orcamento.vendedor_id] || "—") : "—"}
                        </td>
                      )}
                      {visibleOrcCols.includes("valor_projeto") && (
                        <td className={cn(listagemTdCenter, "text-gray-900 font-semibold hidden lg:table-cell whitespace-nowrap")}>
                        R$ {Number(orcamento.valor_total || 0).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                        </td>
                      )}
                      {visibleOrcCols.includes("valor_seguro") && (
                        <td className={cn(listagemTdCenter, "text-gray-900 font-semibold hidden lg:table-cell whitespace-nowrap")}>
                        {orcamento.valor_seguro && orcamento.valor_seguro > 0 
                          ? `R$ ${Number(orcamento.valor_seguro).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}`
                          : '-'
                        }
                        </td>
                      )}
                      {visibleOrcCols.includes("desconto") && (
                        <td className={cn(listagemTdCenter, "text-gray-600 hidden lg:table-cell whitespace-nowrap")}>
                          {(() => {
                            const qtd = Number(orcamento.quantidade_placas) || 0;
                            const valorPainel = Number(orcamento.plano?.valor_por_painel) || 0;
                            const vigencia = Number(orcamento.vigencia_anos) || 1;
                            const mult =
                              vigencia === 2
                                ? 1.8
                                : vigencia === 3
                                  ? 2.5
                                  : vigencia === 5
                                    ? 3.8
                                    : vigencia === 10
                                      ? 6.5
                                      : 1;
                            const tabela =
                              orcamento.valor_tabela != null && Number(orcamento.valor_tabela) > 0
                                ? Number(orcamento.valor_tabela)
                                : qtd > 0 && valorPainel > 0
                                  ? qtd * valorPainel * mult
                                  : 0;
                            const seguro = Number(orcamento.valor_seguro) || 0;
                            let descontoNum = Number(orcamento.desconto_valor) || 0;
                            if (
                              descontoNum <= 0 &&
                              tabela > 0 &&
                              seguro > 0 &&
                              tabela > seguro + 0.009
                            ) {
                              descontoNum = Math.round((tabela - seguro) * 100) / 100;
                            }
                            if (descontoNum <= 0) return "-";
                            const quem = orcamento.desconto_concedido_por
                              ? userNamesMap?.[orcamento.desconto_concedido_por] || "—"
                              : null;
                            return (
                              <div className="flex flex-col items-center leading-tight">
                                <span className="font-semibold text-gray-900">
                                  R$ {descontoNum.toLocaleString("pt-BR", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                                {quem && <span className="text-[10px] text-gray-500">{quem}</span>}
                              </div>
                            );
                          })()}
                        </td>
                      )}
                      {visibleOrcCols.includes("paineis") && (
                        <td className={cn(listagemTdCenter, "text-gray-600 hidden md:table-cell whitespace-nowrap")}>
                        {orcamento.quantidade_placas && orcamento.quantidade_placas > 0 
                          ? `${orcamento.quantidade_placas}` 
                          : '-'
                        }
                        </td>
                      )}
                      {visibleOrcCols.includes("plano") && (
                        <td className={cn(listagemTdCenter, "text-gray-600 hidden md:table-cell")}>
                        {orcamento.plano_id ? (
                          <div className="flex justify-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${getPlanoColor(orcamento.plano_id)}`}>
                              {getPlanoNome(orcamento.plano_id)}
                            </span>
                          </div>
                        ) : '-'
                        }
                        </td>
                      )}
                      {visibleOrcCols.includes("vigencia") && (
                        <td className={cn(listagemTdCenter, "text-gray-600")}>
                        {orcamento.vigencia_anos && orcamento.vigencia_anos > 0 
                          ? `${orcamento.vigencia_anos} ano${orcamento.vigencia_anos > 1 ? 's' : ''}` 
                          : '-'
                        }
                        </td>
                      )}
                      {visibleOrcCols.includes("data") && (
                        <td className={cn(listagemTdCenter, "text-gray-600")}>
                        {new Date(orcamento.created_at).toLocaleDateString('pt-BR')}
                        </td>
                      )}
                      {visibleOrcCols.includes("status") && (
                        <td className={listagemTdCenter}>
                        <div className="flex justify-center">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${getStatusColor(orcamento.status)}`}>
                            {getStatusLabel(orcamento.status)}
                          </span>
                        </div>
                        </td>
                      )}
                      <td className={listagemTdCenter}>
                        {orcamento.status === 'pendente' && orcamento.prazo_aprovacao ? (
                          <div className="flex items-center justify-center gap-1">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span className={`text-xs ${getTempoRestanteColor(orcamento)}`}>
                              {getTempoRestante(orcamento)}
                            </span>
                          </div>
                        ) : orcamento.status === 'aprovado' ? (
                          <span className="text-xs text-green-600">
                            Aprovado em {orcamento.data_aprovacao ? new Date(orcamento.data_aprovacao).toLocaleDateString('pt-BR') : '-'}
                          </span>
                        ) : orcamento.status === 'aguardando_aprovacao_vistoria' ? (
                          <span className="text-xs text-blue-600">Aguardando aprovação (vistoria)</span>
                        ) : (
                          <span className="text-xs text-gray-500">-</span>
                        )}
                      </td>
                      <td className={cn(listagemTdCenter, "whitespace-nowrap")}>
                        <div className="flex items-center justify-center gap-0.5 flex-nowrap">
                          <Button 
                            variant="outline" 
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() => handleEditarOrcamento(orcamento)}
                            title="Editar orçamento completo"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="icon"
                            className="h-8 w-8 shrink-0 text-blue-600 hover:text-blue-700"
                            onClick={() => handleGerarPdf(orcamento)}
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {orcamento.status === 'pendente' && (
                            <>
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="h-8 w-8 shrink-0 text-green-600 hover:text-green-700"
                                onClick={() => handleAprovarOrcamento(orcamento.id)}
                                disabled={isPendingAprovacao}
                                title={isPendingAprovacao ? "Aprovando..." : "Aprovar orçamento"}
                              >
                                {isPendingAprovacao ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                              </Button>
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="h-8 w-8 shrink-0 text-red-600 hover:text-red-700"
                                onClick={() => handleExcluirOrcamento(orcamento.id)}
                                title="Excluir orçamento"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {/* Um único botão: abre modal com lista de anexos da vistoria */}
                          {(orcamento.vistoria_urls?.length ?? 0) > 0 && (
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 shrink-0 text-blue-600 hover:text-blue-700"
                              title={`Anexos da vistoria (${orcamento.vistoria_urls!.length})`}
                              onClick={() => setOrcamentoAnexosVistoria(orcamento)}
                            >
                              <Paperclip className="h-4 w-4" />
                            </Button>
                          )}
                          {orcamento.status === 'aguardando_aprovacao_vistoria' && (
                            <>
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="h-8 w-8 shrink-0 text-green-600 hover:text-green-700"
                                onClick={() => handleAprovarOrcamento(orcamento.id)}
                                disabled={isPendingAprovacao}
                                title="Aprovar orçamento"
                              >
                                {isPendingAprovacao ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                              </Button>
                              {handleRejeitarOrcamentoVistoria && (
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="h-8 w-8 shrink-0 text-red-600 hover:text-red-700"
                                onClick={() => setRejeitarOrcamentoId(orcamento.id)}
                                disabled={isPendingRejeicao}
                                title="Rejeitar orçamento"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {orcamentoExpandido === orcamento.id && (
                      <tr>
                        <td colSpan={11} className="p-0">
                          <div className="bg-gray-50 p-4 border-t">
                            <EquipamentosSelector 
                              orcamentoId={orcamento.id.toString()}
                              disabled={orcamento.status === 'aprovado'}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
                })}
              </tbody>
            </table>
            
            {totalPages > 1 && onPageChange && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <span className="text-gray-700">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </div>
            )}

            {currentItems.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Nenhum orçamento encontrado
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>

      <EditarOrcamentoDialog 
        open={showEditarOrcamento}
        onOpenChange={setShowEditarOrcamento}
        orcamento={orcamentoParaEditar}
      />

      {/* Modal: Anexos da vistoria */}
      <Dialog open={orcamentoAnexosVistoria != null} onOpenChange={(open) => !open && setOrcamentoAnexosVistoria(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5 text-blue-600" />
              Anexos da vistoria
              {orcamentoAnexosVistoria && (
                <span className="text-sm font-normal text-gray-500">
                  Orçamento #{orcamentoAnexosVistoria.id}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {orcamentoAnexosVistoria?.vistoria_urls?.map((v, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-2 py-2 px-2.5 rounded-md border bg-gray-50 hover:bg-gray-100"
              >
                <span className="flex items-center gap-2 min-w-0 truncate text-sm">
                  <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="truncate font-medium" title={v.nome}>{v.nome}</span>
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 text-blue-600"
                    title="Abrir"
                    onClick={() => void openStorageUrl(v.url)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    title="Baixar"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = v.url;
                      link.download = v.nome || `vistoria-${i + 1}`;
                      link.target = '_blank';
                      link.click();
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrcamentoAnexosVistoria(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejeitarOrcamentoId != null} onOpenChange={(open) => !open && setRejeitarOrcamentoId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rejeitar orçamento (vistoria)</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Opcionalmente informe o motivo da rejeição com base na análise da vistoria.
          </p>
          <div className="space-y-2">
            <Label htmlFor="motivo-rejeicao">Motivo (opcional)</Label>
            <Textarea
              id="motivo-rejeicao"
              placeholder="Ex.: Fotos insuficientes, instalação não conforme..."
              value={motivoRejeicao}
              onChange={(e) => setMotivoRejeicao(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejeitarOrcamentoId(null); setMotivoRejeicao(""); }}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={isPendingRejeicao}
              onClick={() => {
                if (rejeitarOrcamentoId != null && handleRejeitarOrcamentoVistoria) {
                  handleRejeitarOrcamentoVistoria(rejeitarOrcamentoId, motivoRejeicao.trim() || undefined);
                  setRejeitarOrcamentoId(null);
                  setMotivoRejeicao("");
                }
              }}
            >
              {isPendingRejeicao ? "Rejeitando..." : "Rejeitar orçamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
